// src/contexts/NotificationsContext.js
// Centralized notification management to reduce WebSocket connections
// Instead of each component creating its own listeners, this context manages
// ONE listener per client and shares the data with all consumers.
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { auth, db } from '../firebase';
import { collection, onSnapshot, doc, updateDoc, writeBatch, query, where } from 'firebase/firestore';
import { useSessionStorage } from './SessionStorageContext';
import {
  isCurrentDashboardActionNotification,
  isCampaignLevelActionNotification,
  isRenderableDashboardActionNotification,
  getDashboardNotificationDisplayOrder,
  selectPreferredCurrentActionNotification,
  selectCurrentDashboardActionNotification
} from '../utils/actionNotifications';
import { countPendingOutboxByClient, summarizeBlockedOutboxByClient } from '../utils/outboxCounts';
import { isMailboxConnected } from '../utils/mailboxConnection';
import { resolveCampaignCompletion } from '../utils/campaignCompletion';
import { getThreadPropertyKey } from '../utils/threadGrouping';
import { readDashboardTimestampMillis } from '../utils/dashboardFilters';
import { isNextVersionSchedulingRecord } from '../utils/baseV1Isolation';
import { dismissNotificationOnClient } from '../utils/dismissNotificationOnClient';

const NotificationsContext = createContext();

export function buildThreadOrder(documents = []) {
  const visibleDocuments = documents.filter(docSnap => (
    !isNextVersionSchedulingRecord(docSnap.data() || {})
  ));
  const groupedByProperty = {};

  visibleDocuments.forEach(docSnap => {
    const data = docSnap.data();
    const normalizedKey = getThreadPropertyKey(data.subject);

    if (!groupedByProperty[normalizedKey]) {
      groupedByProperty[normalizedKey] = {
        propertyAddress: normalizedKey,
        threadIds: [],
        latestUpdate: null
      };
    }

    groupedByProperty[normalizedKey].threadIds.push(docSnap.id);
    const threadUpdate = data.updatedAt?.toDate?.() || new Date(data.updatedAt || 0);
    if (!groupedByProperty[normalizedKey].latestUpdate ||
        threadUpdate > groupedByProperty[normalizedKey].latestUpdate) {
      groupedByProperty[normalizedKey].latestUpdate = threadUpdate;
    }
  });

  const threadOrder = {};
  Object.values(groupedByProperty)
    .sort((a, b) => (b.latestUpdate || 0) - (a.latestUpdate || 0))
    .forEach((group, index) => {
      threadOrder[`prop:${group.propertyAddress.toLowerCase()}`] = index;
      group.threadIds.forEach(threadId => {
        threadOrder[threadId] = index;
        threadOrder[`threadProp:${threadId}`] = group.propertyAddress;
      });
    });

  visibleDocuments.forEach(docSnap => {
    const data = docSnap.data();
    threadOrder[`threadStatus:${docSnap.id}`] = data.status || 'active';
    // 'terminal_stop' means the whole campaign was stopped; 'user_requested'
    // means this one conversation was. The dashboard treats their open cards
    // differently, so the reason travels with the status.
    if (data.statusReason) threadOrder[`threadStatusReason:${docSnap.id}`] = data.statusReason;
    if (data.rowNumber != null) threadOrder[`threadRow:${docSnap.id}`] = data.rowNumber;
  });

  return threadOrder;
}

export function calculateDashboardStats({
  clients = [],
  clientNotifications = {},
  clientThreads = {},
  totalNotificationCount = 0,
  clientOutboxCounts = {},
  clientBlockedOutbox = {},
  isLoading = false
} = {}) {
  let actionsNeeded = 0;
  let sheetUpdates = 0;
  let propertiesCompleted = 0;
  let nonViableProperties = 0;
  let completedCampaigns = 0;
  const clientCampaignCompletion = {};

  const getStartedCount = clients.filter(client => client.status === 'new').length;
  // Outreach the send path refused is work nobody has dealt with yet, so it
  // belongs in the same header count as everything else awaiting a decision.
  // Without this the count dropped to zero and the campaign looked finished.
  const blockedOutboxCount = Object.values(clientBlockedOutbox || {})
    .reduce((sum, entry) => sum + (entry?.count || 0), 0);
  actionsNeeded = getStartedCount + blockedOutboxCount;

  const clientCompletedCount = {};
  const clientUnavailableCount = {};
  const clientLatestResolvedAt = {};

  Object.entries(clientNotifications).forEach(([clientId, data]) => {
    clientCompletedCount[clientId] = 0;
    clientUnavailableCount[clientId] = 0;
    clientLatestResolvedAt[clientId] = 0;

    (data.notifications || []).forEach(notif => {
      if (isNextVersionSchedulingRecord(notif)) return;
      if (notif.kind === 'action_needed') {
        const threadLookup = Object.prototype.hasOwnProperty.call(clientThreads, clientId)
          ? clientThreads[clientId]
          : undefined;
        if (isRenderableDashboardActionNotification(notif, threadLookup)) {
          actionsNeeded++;
        }
      } else if (notif.kind === 'sheet_update') {
        sheetUpdates++;
      } else if (notif.kind === 'row_completed') {
        propertiesCompleted++;
        clientCompletedCount[clientId]++;
        clientLatestResolvedAt[clientId] = Math.max(
          clientLatestResolvedAt[clientId],
          readDashboardTimestampMillis(notif.createdAt || notif.updatedAt)
        );
      } else if (notif.kind === 'property_unavailable') {
        nonViableProperties++;
        clientUnavailableCount[clientId]++;
        clientLatestResolvedAt[clientId] = Math.max(
          clientLatestResolvedAt[clientId],
          readDashboardTimestampMillis(notif.createdAt || notif.updatedAt)
        );
      }
    });
  });

  clients.forEach(client => {
    const completion = resolveCampaignCompletion(client, {
      completed: clientCompletedCount[client.id] || 0,
      unavailable: clientUnavailableCount[client.id] || 0
    });

    clientCampaignCompletion[client.id] = completion.isCampaignComplete && clientLatestResolvedAt[client.id]
      ? {
          ...completion,
          completedAt: new Date(clientLatestResolvedAt[client.id]).toISOString()
        }
      : completion;
    if (completion.isCampaignComplete) {
      completedCampaigns++;
    }
  });

  return {
    dashboardStats: {
      actionsNeeded,
      sheetUpdates,
      propertyMatches: propertiesCompleted,
      nonViableProperties,
      completedCampaigns,
      totalNotifications: totalNotificationCount,
      importantNotifications: Object.values(clientNotifications).filter(d => d.hasImportant).length,
      pendingOutboxCount: Object.values(clientOutboxCounts).reduce((sum, count) => sum + count, 0),
      loading: isLoading
    },
    clientCampaignCompletion
  };
}

export const useNotificationsContext = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotificationsContext must be used within a NotificationsProvider');
  }
  return context;
};

export const NotificationsProvider = ({ children }) => {
  const { sessionData } = useSessionStorage();
  const clients = useMemo(() => sessionData.clients || [], [sessionData.clients]);
  const currentUid = auth.currentUser?.uid;

  // Per-client notification data
  // { [clientId]: { notifications: [], count: number, hasImportant: boolean, newPropertyRequest: null | object } }
  const [clientNotifications, setClientNotifications] = useState({});

  // Per-client thread data for ordering (threadId -> { updatedAt, subject })
  const [clientThreads, setClientThreads] = useState({});
  const [clientOutboxCounts, setClientOutboxCounts] = useState({});
  const [clientBlockedOutbox, setClientBlockedOutbox] = useState({});

  // Whether this account's mailbox is linked at all. One listener on the user
  // document, shared by every surface that has to say so.
  const [mailboxUser, setMailboxUser] = useState(null);
  const [mailboxLoading, setMailboxLoading] = useState(Boolean(currentUid));
  const mailboxUnsubscriberRef = useRef(null);

  // Loading state
  const [isLoading, setIsLoading] = useState(true);

  // Track active listeners to prevent duplicates
  const activeListenersRef = useRef(new Set());
  const unsubscribersRef = useRef({});
  const threadUnsubscribersRef = useRef({});
  const outboxUnsubscriberRef = useRef(null);

  useEffect(() => {
    outboxUnsubscriberRef.current?.();
    outboxUnsubscriberRef.current = null;

    if (!currentUid) {
      setClientOutboxCounts({});
      setClientBlockedOutbox({});
      return undefined;
    }

    const outboxRef = collection(db, 'users', currentUid, 'outbox');
    const unsubscribe = onSnapshot(outboxRef, (snapshot) => {
      const items = snapshot.docs
        .map(docSnap => docSnap.data() || {})
        .filter(item => !isNextVersionSchedulingRecord(item));
      setClientOutboxCounts(countPendingOutboxByClient(items));
      setClientBlockedOutbox(summarizeBlockedOutboxByClient(items));
    }, (error) => {
      console.error('Failed to listen for pending outbox counts:', error);
      setClientOutboxCounts({});
      setClientBlockedOutbox({});
    });

    outboxUnsubscriberRef.current = unsubscribe;
    return () => {
      unsubscribe?.();
      if (outboxUnsubscriberRef.current === unsubscribe) {
        outboxUnsubscriberRef.current = null;
      }
    };
  }, [currentUid]);

  useEffect(() => {
    mailboxUnsubscriberRef.current?.();
    mailboxUnsubscriberRef.current = null;

    if (!currentUid) {
      setMailboxUser(null);
      setMailboxLoading(false);
      return undefined;
    }

    setMailboxLoading(true);
    const userRef = doc(db, 'users', currentUid);
    const unsubscribe = onSnapshot(userRef, (snapshot) => {
      // A user document that does not exist reads back as no data, which is the
      // same answer as a document with no token flag: not connected.
      setMailboxUser(snapshot?.data?.() || {});
      setMailboxLoading(false);
    }, (error) => {
      // A read we could not make is not proof the mailbox is broken, so the
      // banner stays hidden rather than nagging an advisor who is fine.
      console.error('Failed to listen for the mailbox connection state:', error);
      setMailboxUser(null);
      setMailboxLoading(false);
    });

    mailboxUnsubscriberRef.current = unsubscribe;
    return () => {
      unsubscribe?.();
      if (mailboxUnsubscriberRef.current === unsubscribe) {
        mailboxUnsubscriberRef.current = null;
      }
    };
  }, [currentUid]);

  const mailboxConnection = useMemo(() => ({
    connected: mailboxUser === null ? true : isMailboxConnected(mailboxUser),
    loading: mailboxLoading
  }), [mailboxUser, mailboxLoading]);

  // Set up ONE listener per client
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) {
      setIsLoading(false);
      return;
    }

    if (!clients || clients.length === 0) {
      // Clean up any existing listeners
      Object.values(unsubscribersRef.current).forEach(unsub => unsub?.());
      unsubscribersRef.current = {};
      activeListenersRef.current = new Set();
      setClientNotifications({});
      setIsLoading(false);
      return;
    }

    const currentClientIds = new Set(clients.map(c => c.id));

    // Remove listeners for clients that no longer exist
    for (const clientId of activeListenersRef.current) {
      if (!currentClientIds.has(clientId)) {
        unsubscribersRef.current[clientId]?.();
        delete unsubscribersRef.current[clientId];
        activeListenersRef.current.delete(clientId);
        setClientNotifications(prev => {
          const next = { ...prev };
          delete next[clientId];
          return next;
        });
      }
    }

    // Add listeners for new clients
    for (const client of clients) {
      if (!client.id || activeListenersRef.current.has(client.id)) continue;

      const notificationsRef = collection(db, 'users', uid, 'clients', client.id, 'notifications');

      const unsubscribe = onSnapshot(notificationsRef, (snapshot) => {
        const notifications = [];
        let hasImportant = false;
        let newPropertyRequest = null;
        let actionableNotification = null;

        snapshot.docs.forEach(docSnap => {
          const data = { id: docSnap.id, ...docSnap.data() };
          if (isNextVersionSchedulingRecord(data)) return;
          notifications.push(data);

          if (data.priority === 'important') {
            hasImportant = true;
          }

          // Check for new property request (pending send)
          if (data.kind === 'action_needed' && data.meta?.reason === 'new_property_pending_send') {
            newPropertyRequest = data;
          }

        });

        actionableNotification = selectPreferredCurrentActionNotification(
          notifications.filter(isCurrentDashboardActionNotification)
        );

        setClientNotifications(prev => ({
          ...prev,
          [client.id]: {
            notifications,
            count: notifications.length,
            hasImportant,
            newPropertyRequest,
            actionableNotification
          }
        }));
      }, (error) => {
        if (error.code !== 'permission-denied') {
          console.error(`NotificationsContext: Error for client ${client.id}:`, error);
        }
      });

      unsubscribersRef.current[client.id] = unsubscribe;
      activeListenersRef.current.add(client.id);

      // Also set up thread listener for ordering
      // Must match ConversationsPanel logic: group by property, sort by latest update
      const threadsRef = collection(db, 'users', uid, 'threads');
      const threadsQuery = query(
        threadsRef,
        where('clientId', '==', client.id)
      );

      const threadUnsub = onSnapshot(threadsQuery, (snapshot) => {
        const threadOrder = buildThreadOrder(snapshot.docs);

        setClientThreads(prev => ({
          ...prev,
          [client.id]: threadOrder
        }));
      }, (error) => {
        if (error.code !== 'permission-denied') {
          console.error(`NotificationsContext: Thread error for client ${client.id}:`, error);
        }
      });

      threadUnsubscribersRef.current[client.id] = threadUnsub;
    }

    setIsLoading(false);

    // Cleanup on unmount
    return () => {
      Object.values(unsubscribersRef.current).forEach(unsub => unsub?.());
      Object.values(threadUnsubscribersRef.current).forEach(unsub => unsub?.());
      unsubscribersRef.current = {};
      threadUnsubscribersRef.current = {};
      activeListenersRef.current = new Set();
    };
  }, [clients]);

  // Computed: total notification count across all clients
  const totalNotificationCount = useMemo(() => {
    return Object.values(clientNotifications).reduce((sum, data) => sum + data.count, 0);
  }, [clientNotifications]);

  // Computed: has any important notification
  const hasImportantNotifications = useMemo(() => {
    return Object.values(clientNotifications).some(data => data.hasImportant);
  }, [clientNotifications]);

  // Computed: map of clientId -> count (for ClientsTable)
  const clientNotificationCounts = useMemo(() => {
    const counts = {};
    Object.entries(clientNotifications).forEach(([clientId, data]) => {
      counts[clientId] = data.count;
    });
    return counts;
  }, [clientNotifications]);

  // Computed: map of clientId -> hasNotifications boolean (for filtering)
  const clientHasNotifications = useMemo(() => {
    const map = {};
    Object.entries(clientNotifications).forEach(([clientId, data]) => {
      map[clientId] = data.count > 0;
    });
    return map;
  }, [clientNotifications]);

  const clientCurrentActionCounts = useMemo(() => {
    const counts = {};
    Object.entries(clientNotifications).forEach(([clientId, data]) => {
      const threadLookup = Object.prototype.hasOwnProperty.call(clientThreads, clientId)
        ? clientThreads[clientId]
        : undefined;
      counts[clientId] = (data.notifications || []).filter(notif =>
        isRenderableDashboardActionNotification(notif, threadLookup)
      ).length;
    });
    return counts;
  }, [clientNotifications, clientThreads]);

  const clientHasCurrentActions = useMemo(() => {
    const map = {};
    Object.entries(clientCurrentActionCounts).forEach(([clientId, count]) => {
      map[clientId] = count > 0;
    });
    return map;
  }, [clientCurrentActionCounts]);

  const clientPendingSendCounts = useMemo(() => {
    const counts = { ...clientOutboxCounts };
    Object.entries(clientNotifications).forEach(([clientId, data]) => {
      const notificationPendingCount = (data.notifications || []).filter(notif =>
        notif.kind === 'action_needed' && notif.meta?.reason === 'new_property_pending_send'
      ).length;
      counts[clientId] = (counts[clientId] || 0) + notificationPendingCount;
    });
    return counts;
  }, [clientNotifications, clientOutboxCounts]);

  // Computed: new property requests by client
  const newPropertyRequests = useMemo(() => {
    const requests = {};
    Object.entries(clientNotifications).forEach(([clientId, data]) => {
      if (data.newPropertyRequest) {
        requests[clientId] = data.newPropertyRequest;
      }
    });
    return requests;
  }, [clientNotifications]);

  // Computed: actionable notifications by client (highest priority action_needed per client)
  const actionableNotifications = useMemo(() => {
    const actions = {};
    Object.entries(clientNotifications).forEach(([clientId, data]) => {
      const threadLookup = Object.prototype.hasOwnProperty.call(clientThreads, clientId)
        ? clientThreads[clientId]
        : undefined;
      const action = selectCurrentDashboardActionNotification(
        data.notifications || [],
        threadLookup
      );

      if (action) {
        actions[clientId] = action;
      }
    });
    return actions;
  }, [clientNotifications, clientThreads]);

  // Computed: ALL actionable notifications by client (sorted by property group order, matching conversation panel)
  const allActionableNotifications = useMemo(() => {
    const allActions = {};

    Object.entries(clientNotifications).forEach(([clientId, data]) => {
      const orderLookup = clientThreads[clientId] || {};

      // Get display order for a notification (lower = appears first in panel)
      const getDisplayOrder = notif => getDashboardNotificationDisplayOrder(notif, orderLookup) ?? 9999;

      // Get property key for deduplication
      const getPropertyKey = (notif) => {
        // Campaign cards are separate decisions, including cards for the same row.
        if (isCampaignLevelActionNotification(notif)) return `id:${notif.id}`;
        const threadId = notif.threadId || notif.meta?.threadId;
        if (threadId) return `thread:${threadId}`;
        const rowNumber = notif.rowNumber ?? notif.meta?.rowNumber;
        if (rowNumber != null) return `row:${rowNumber}`;
        const anchor = notif.rowAnchor || notif.meta?.rowAnchor || notif.meta?.propertyAddress ||
          notif.meta?.address || notif.meta?.property?.address;
        if (anchor) return `prop:${anchor.toLowerCase().trim()}`;
        const order = getDashboardNotificationDisplayOrder(notif, orderLookup);
        if (order !== null) return `order:${order}`;
        return `id:${notif.id}`; // Fallback to notification ID
      };

      const hasThreadLookup = Object.prototype.hasOwnProperty.call(clientThreads, clientId);
      const threadLookup = hasThreadLookup ? orderLookup : undefined;

      const actionsByProperty = new Map();
      data.notifications
        .filter(notif => isRenderableDashboardActionNotification(notif, threadLookup))
        .forEach(notif => {
          const propKey = getPropertyKey(notif);
          const propertyActions = actionsByProperty.get(propKey) || [];
          propertyActions.push(notif);
          actionsByProperty.set(propKey, propertyActions);
        });

      const deduplicatedNotifs = [...actionsByProperty.values()]
        .map(propertyActions => selectPreferredCurrentActionNotification(propertyActions))
        .filter(Boolean)
        .sort((a, b) => getDisplayOrder(a) - getDisplayOrder(b));

      if (deduplicatedNotifs.length > 0) {
        allActions[clientId] = deduplicatedNotifs;
      }
    });
    return allActions;
  }, [clientNotifications, clientThreads]);

  const { dashboardStats, clientCampaignCompletion } = useMemo(() => {
    return calculateDashboardStats({
      clients,
      clientNotifications,
      clientThreads,
      totalNotificationCount,
      clientOutboxCounts,
      clientBlockedOutbox,
      isLoading
    });
  }, [clientNotifications, clientThreads, clients, totalNotificationCount, isLoading, clientOutboxCounts, clientBlockedOutbox]);

  // Get notifications for a specific client
  const getClientNotifications = useCallback((clientId) => {
    return clientNotifications[clientId]?.notifications || [];
  }, [clientNotifications]);

  // Get notification counts for a specific client
  const getClientCounts = useCallback((clientId) => {
    const data = clientNotifications[clientId];
    if (!data) return { general: 0, important: 0, total: 0 };

    let general = 0;
    let important = 0;

    data.notifications.forEach(notif => {
      if (notif.kind === 'action_needed' && notif.meta?.reason === 'new_property_pending_send') {
        // Don't count in either category
      } else if (notif.priority === 'important') {
        important++;
      } else {
        general++;
      }
    });

    return { general, important, total: data.count };
  }, [clientNotifications]);

  // Get new property request for a specific client
  const getNewPropertyRequest = useCallback((clientId) => {
    return clientNotifications[clientId]?.newPropertyRequest || null;
  }, [clientNotifications]);

  // Record dismissal and remove this notification with its counter update.
  // This does not stop the thread or change its outbox or sheet state.
  // The Firestore listener removes the card from the UI after deletion.
  const clearNotification = useCallback(async (clientId, notificationId) => {
    try {
      const uid = auth.currentUser?.uid;
      if (!uid) return false;
      const notification = clientNotifications[clientId]?.notifications?.find(
        n => n.id === notificationId
      ) || { id: notificationId };

      return await dismissNotificationOnClient({
        uid,
        clientId,
        client: clients.find(client => client.id === clientId) || { id: clientId },
        notificationId,
        notification
      });
    } catch (error) {
      console.error('Failed to clear notification:', error);
      return false;
    }
  }, [clientNotifications, clients]);

  // Clear all notifications for a client
  const clearAllClientNotifications = useCallback(async (clientId) => {
    try {
      const uid = auth.currentUser?.uid;
      const notifications = clientNotifications[clientId]?.notifications || [];
      if (!uid || notifications.length === 0) return false;
      if (notifications.some(notification => notification.kind === 'action_needed')) {
        return false;
      }

      const batch = writeBatch(db);

      notifications.forEach(notification => {
        const notificationRef = doc(db, 'users', uid, 'clients', clientId, 'notifications', notification.id);
        batch.delete(notificationRef);
      });

      await batch.commit();

      // Reset client notification counters
      const clientRef = doc(db, 'users', uid, 'clients', clientId);
      await updateDoc(clientRef, {
        notificationsUnread: 0,
        newUpdateCount: 0,
        notifCounts: {}
      });

      return true;
    } catch (error) {
      console.error('Failed to clear notifications:', error);
      return false;
    }
  }, [clientNotifications]);

  // Filter notifications by kind
  const getNotificationsByKind = useCallback((clientId, kind) => {
    const notifications = clientNotifications[clientId]?.notifications || [];
    return notifications.filter(n => n.kind === kind);
  }, [clientNotifications]);

  // Filter action_needed by reason
  const getActionNeededByReason = useCallback((clientId, reason) => {
    const notifications = clientNotifications[clientId]?.notifications || [];
    return notifications.filter(n =>
      n.kind === 'action_needed' && n.meta?.reason === reason
    );
  }, [clientNotifications]);

  const value = useMemo(() => ({
    // Per-client data accessors
    getClientNotifications,
    getClientCounts,
    getNewPropertyRequest,
    getNotificationsByKind,
    getActionNeededByReason,

    // Computed aggregates
    totalNotificationCount,
    hasImportantNotifications,
    clientNotificationCounts,
    clientHasNotifications,
    clientCurrentActionCounts,
    clientHasCurrentActions,
    clientPendingSendCounts,
    clientOutboxCounts,
    clientBlockedOutbox,
    mailboxConnection,
    newPropertyRequests,
    actionableNotifications,
    allActionableNotifications,
    dashboardStats,
    clientCampaignCompletion,

    // Actions
    clearNotification,
    clearAllClientNotifications,

    // State
    isLoading,

    // Raw data (for advanced use cases)
    clientNotifications,
  }), [
    getClientNotifications,
    getClientCounts,
    getNewPropertyRequest,
    getNotificationsByKind,
    getActionNeededByReason,
    totalNotificationCount,
    hasImportantNotifications,
    clientNotificationCounts,
    clientHasNotifications,
    clientCurrentActionCounts,
    clientHasCurrentActions,
    clientPendingSendCounts,
    clientOutboxCounts,
    clientBlockedOutbox,
    mailboxConnection,
    newPropertyRequests,
    actionableNotifications,
    allActionableNotifications,
    dashboardStats,
    clientCampaignCompletion,
    clearNotification,
    clearAllClientNotifications,
    isLoading,
    clientNotifications,
  ]);

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

// Backward-compatible hook for components that use useNotifications(clientId)
export const useNotifications = (clientId) => {
  const context = useNotificationsContext();

  return {
    notifications: context.getClientNotifications(clientId),
    counts: context.getClientCounts(clientId),
    newPropertyRequest: context.getNewPropertyRequest(clientId),
    loading: context.isLoading,
    clearAllNotifications: () => context.clearAllClientNotifications(clientId),
    clearNotification: (notificationId) => context.clearNotification(clientId, notificationId),
    getNotificationsByKind: (kind) => context.getNotificationsByKind(clientId, kind),
    getActionNeededByReason: (reason) => context.getActionNeededByReason(clientId, reason),
  };
};

// Provider-tolerant on purpose: a page can ask whether the mailbox is connected
// without being rebuilt around this context, and one rendered outside it simply
// shows no banner instead of throwing.
const MAILBOX_CONNECTION_UNKNOWN = { connected: true, loading: true };

export const useMailboxConnection = () => {
  const context = useContext(NotificationsContext);
  return context?.mailboxConnection || MAILBOX_CONNECTION_UNKNOWN;
};

// Backward-compatible hook for useDashboardStats
export const useDashboardStats = () => {
  const context = useNotificationsContext();
  return context.dashboardStats;
};
